<template>
    <div class="markdown-pdf-container">
        <!-- 顶部工具栏 -->
        <div class="toolbar">
            <div class="toolbar-left">
                <h3 class="title">Markdown to PDF</h3>
                <div class="stats">
                    <span class="stat-item">{{ wordCount }} words</span>
                    <span class="stat-item">{{ lineCount }} lines</span>
                </div>
            </div>
            <div class="toolbar-right">
                <el-button
                    size="small"
                    @click="clearContent"
                    icon="Delete"
                    :disabled="!markdown.trim()"
                >
                    Clear
                </el-button>
                <el-button
                    size="small"
                    @click="importFile"
                    icon="Upload"
                >
                    Import
                </el-button>
                <el-button
                    type="primary"
                    size="small"
                    @click="convertToPdf"
                    :loading="isConverting"
                    :disabled="!markdown.trim()"
                    icon="Download"
                >
                    {{ isConverting ? 'Converting...' : 'Export PDF' }}
                </el-button>
            </div>
        </div>

        <!-- 主要内容区域 -->
        <div class="container">
            <div class="editor">
                <div class="editor-header">
                    <span class="editor-label">Markdown Editor</span>
                </div>
                <textarea
                    v-model="markdown"
                    placeholder="# Welcome to Markdown Editor&#10;&#10;Start typing your markdown content here..."
                    @input="handleInput"
                    ref="textarea"
                ></textarea>
            </div>
            <div class="md-viewer">
                <div class="viewer-header">
                    <span class="viewer-label">Preview</span>
                </div>
                <div class="viewer-content" v-html="renderedMarkdown" ref="content"></div>
            </div>
        </div>

        <!-- 隐藏的文件输入 -->
        <input
            type="file"
            ref="fileInput"
            @change="handleFileImport"
            accept=".md,.txt"
            style="display: none;"
        >
    </div>
</template>

<script>
import hljs from 'highlight.js';
import 'highlight.js/styles/github.css';
import jsPDF from 'jspdf';
import { marked } from 'marked';
import { ElMessage } from 'element-plus';

export default {
    data() {
        return {
            markdown: '',
            renderedMarkdown: '',
            isConverting: false,
            debounceTimer: null
        };
    },
    computed: {
        wordCount() {
            return this.markdown.trim().split(/\s+/).filter(word => word.length > 0).length;
        },
        lineCount() {
            return this.markdown.split('\n').length;
        }
    },
    methods: {
        async convertToPdf() {
            if (!this.markdown.trim()) {
                ElMessage.warning('Please enter some markdown content before converting to PDF.');
                return;
            }

            this.isConverting = true;

            try {
                ElMessage.info('Parsing markdown content...');

                // 解析Markdown内容为结构化数据
                const parsedContent = this.parseMarkdownForPDF(this.markdown);

                ElMessage.info('Generating PDF...');

                // 使用原生jsPDF直接生成PDF
                await this.generatePDFFromParsedContent(parsedContent);

            } catch (error) {
                console.error('PDF conversion failed:', error);
                ElMessage.error(`Failed to convert to PDF: ${error.message || 'Unknown error'}`);
            } finally {
                this.isConverting = false;
            }
        },

        parseMarkdownForPDF(markdown) {
            const lines = markdown.split('\n');
            const content = [];
            let currentCodeBlock = null;
            let currentTable = null;

            for (let i = 0; i < lines.length; i++) {
                const line = lines[i];

                // 处理代码块
                if (line.startsWith('```')) {
                    // 先结束表格（如果有的话）
                    if (currentTable) {
                        content.push(currentTable);
                        currentTable = null;
                    }

                    if (currentCodeBlock) {
                        // 结束代码块
                        content.push({
                            type: 'code',
                            content: currentCodeBlock.join('\n'),
                            language: currentCodeBlock.language || ''
                        });
                        currentCodeBlock = null;
                    } else {
                        // 开始代码块
                        currentCodeBlock = [];
                        currentCodeBlock.language = line.substring(3).trim();
                    }
                    continue;
                }

                if (currentCodeBlock) {
                    currentCodeBlock.push(line);
                    continue;
                }

                // 处理表格
                if (line.includes('|') && line.trim()) {
                    if (!currentTable) {
                        currentTable = {
                            type: 'table',
                            headers: [],
                            rows: []
                        };
                    }

                    const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell);

                    // 检查是否是分割线（如 |---|---|）
                    if (cells.every(cell => cell.match(/^-+$/))) {
                        continue; // 跳过分割线
                    }

                    if (currentTable.headers.length === 0) {
                        currentTable.headers = cells;
                    } else {
                        currentTable.rows.push(cells);
                    }
                    continue;
                } else if (currentTable) {
                    // 表格结束
                    content.push(currentTable);
                    currentTable = null;
                }

                // 处理标题
                if (line.startsWith('#')) {
                    const level = line.match(/^#+/)[0].length;
                    const text = line.replace(/^#+\s*/, '');
                    content.push({
                        type: 'heading',
                        level: level,
                        content: text
                    });
                }
                // 处理列表
                else if (line.match(/^\s*[-*+]\s/)) {
                    const text = line.replace(/^\s*[-*+]\s/, '');
                    content.push({
                        type: 'list',
                        content: text
                    });
                }
                // 处理引用
                else if (line.startsWith('>')) {
                    const text = line.replace(/^>\s*/, '');
                    content.push({
                        type: 'quote',
                        content: text
                    });
                }
                // 处理普通段落
                else if (line.trim()) {
                    content.push({
                        type: 'paragraph',
                        content: line.trim()
                    });
                }
                // 处理空行
                else {
                    content.push({
                        type: 'break',
                        content: ''
                    });
                }
            }

            // 处理最后可能未结束的表格
            if (currentTable) {
                content.push(currentTable);
            }

            return content;
        },

        async generatePDFFromParsedContent(content) {
            const pdf = new jsPDF();

            // PDF设置
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 20;
            const lineHeight = 7;
            let currentY = margin;

            // 字体设置
            pdf.setFont('helvetica');

            for (let i = 0; i < content.length; i++) {
                const item = content[i];

                // 检查是否需要新页
                if (currentY > pageHeight - margin) {
                    pdf.addPage();
                    currentY = margin;
                }

                switch (item.type) {
                    case 'heading':
                        currentY = this.addHeading(pdf, item, currentY, margin, pageWidth);
                        break;
                    case 'paragraph':
                        currentY = this.addParagraph(pdf, item, currentY, margin, pageWidth);
                        break;
                    case 'list':
                        currentY = this.addListItem(pdf, item, currentY, margin, pageWidth);
                        break;
                    case 'quote':
                        currentY = this.addQuote(pdf, item, currentY, margin, pageWidth);
                        break;
                    case 'code':
                        currentY = this.addCodeBlock(pdf, item, currentY, margin, pageWidth, pageHeight);
                        break;
                    case 'table':
                        currentY = this.addTable(pdf, item, currentY, margin, pageWidth, pageHeight);
                        break;
                    case 'break':
                        currentY += lineHeight / 2;
                        break;
                }
            }

            const fileName = `markdown-document-${new Date().getTime()}.pdf`;
            pdf.save(fileName);
            ElMessage.success(`PDF exported successfully: ${fileName}`);
        },

        addHeading(pdf, item, currentY, margin, pageWidth) {
            const fontSize = Math.max(20 - (item.level * 2), 12);
            pdf.setFontSize(fontSize);
            pdf.setFont('helvetica', 'bold');

            currentY += fontSize * 0.5;
            pdf.text(item.content, margin, currentY);
            currentY += fontSize * 0.8;

            // 添加下划线（对于h1和h2）
            if (item.level <= 2) {
                pdf.setLineWidth(0.5);
                pdf.line(margin, currentY, pageWidth - margin, currentY);
                currentY += 5;
            }

            pdf.setFont('helvetica', 'normal');
            return currentY + 5;
        },

        addParagraph(pdf, item, currentY, margin, pageWidth) {
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'normal');

            // 处理Markdown内联语法（粗体、斜体、代码）
            const processedText = this.processInlineMarkdown(item.content);

            // 处理文本换行
            const textLines = pdf.splitTextToSize(processedText.text, pageWidth - 2 * margin);

            for (let line of textLines) {
                pdf.text(line, margin, currentY);
                currentY += 7;
            }

            return currentY + 3;
        },

        processInlineMarkdown(text) {
            // 简化处理：移除Markdown标记，后续可以扩展支持样式
            let processedText = text
                .replace(/\*\*(.*?)\*\*/g, '$1')  // 粗体
                .replace(/\*(.*?)\*/g, '$1')      // 斜体
                .replace(/`(.*?)`/g, '$1')        // 行内代码
                .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1'); // 链接

            return { text: processedText };
        },

        addListItem(pdf, item, currentY, margin, pageWidth) {
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'normal');

            // 添加项目符号
            pdf.text('•', margin, currentY);

            // 处理文本换行
            const textLines = pdf.splitTextToSize(item.content, pageWidth - 2 * margin - 10);

            for (let i = 0; i < textLines.length; i++) {
                pdf.text(textLines[i], margin + 10, currentY);
                if (i < textLines.length - 1) currentY += 7;
            }

            return currentY + 5;
        },

        addQuote(pdf, item, currentY, margin, pageWidth) {
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'italic');

            // 添加引用线
            pdf.setLineWidth(2);
            pdf.setDrawColor(128, 128, 128);
            pdf.line(margin, currentY - 3, margin, currentY + 10);

            // 处理文本换行
            const textLines = pdf.splitTextToSize(item.content, pageWidth - 2 * margin - 15);

            for (let line of textLines) {
                pdf.text(line, margin + 15, currentY);
                currentY += 7;
            }

            pdf.setFont('helvetica', 'normal');
            pdf.setDrawColor(0, 0, 0);
            return currentY + 3;
        },

        addCodeBlock(pdf, item, currentY, margin, pageWidth, pageHeight) {
            pdf.setFontSize(10);
            pdf.setFont('courier', 'normal');

            // 添加背景矩形
            pdf.setFillColor(245, 245, 245);
            const codeLines = item.content.split('\n');
            const blockHeight = codeLines.length * 6 + 10;

            // 检查代码块是否需要分页
            if (currentY + blockHeight > pageHeight - margin) {
                pdf.addPage();
                currentY = margin;
            }

            pdf.rect(margin, currentY - 5, pageWidth - 2 * margin, blockHeight, 'F');

            // 添加代码文本
            for (let line of codeLines) {
                pdf.text(line, margin + 5, currentY);
                currentY += 6;
            }

            pdf.setFont('helvetica', 'normal');
            return currentY + 8;
        },

        addTable(pdf, item, currentY, margin, pageWidth, pageHeight) {
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');

            const tableWidth = pageWidth - 2 * margin;
            const colWidth = tableWidth / item.headers.length;
            const rowHeight = 15;

            // 检查表格是否需要分页
            const tableHeight = (item.rows.length + 1) * rowHeight + 10;
            if (currentY + tableHeight > pageHeight - margin) {
                pdf.addPage();
                currentY = margin;
            }

            // 绘制表头
            pdf.setFont('helvetica', 'bold');
            pdf.setFillColor(240, 240, 240);

            for (let i = 0; i < item.headers.length; i++) {
                const x = margin + i * colWidth;
                // 绘制表头背景
                pdf.rect(x, currentY, colWidth, rowHeight, 'F');
                // 绘制表头边框
                pdf.rect(x, currentY, colWidth, rowHeight);
                // 绘制表头文本
                pdf.text(item.headers[i], x + 2, currentY + 10);
            }

            currentY += rowHeight;

            // 绘制表格数据
            pdf.setFont('helvetica', 'normal');
            pdf.setFillColor(255, 255, 255);

            for (let row of item.rows) {
                for (let i = 0; i < row.length && i < item.headers.length; i++) {
                    const x = margin + i * colWidth;
                    // 绘制单元格背景
                    pdf.rect(x, currentY, colWidth, rowHeight, 'F');
                    // 绘制单元格边框
                    pdf.rect(x, currentY, colWidth, rowHeight);
                    // 绘制单元格文本
                    const cellText = row[i] || '';
                    const truncatedText = cellText.length > 15 ? cellText.substring(0, 12) + '...' : cellText;
                    pdf.text(truncatedText, x + 2, currentY + 10);
                }
                currentY += rowHeight;
            }

            return currentY + 10;
        },

        handleInput() {
            // 防抖处理，避免频繁渲染
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }

            this.debounceTimer = setTimeout(() => {
                this.renderMarkdown();
            }, 300);
        },

        renderMarkdown() {
            try {
                this.renderedMarkdown = marked(this.markdown);
                this.$nextTick(() => {
                    this.highlightCode();
                });
            } catch (error) {
                console.error('Markdown rendering error:', error);
                ElMessage.error('Failed to render markdown content');
            }
        },

        clearContent() {
            this.$confirm('Are you sure you want to clear all content?', 'Clear Content', {
                type: 'warning'
            }).then(() => {
                this.markdown = '';
                this.renderedMarkdown = '';
                ElMessage.success('Content cleared');
            }).catch(() => {
                // 用户取消
            });
        },

        importFile() {
            this.$refs.fileInput.click();
        },

        handleFileImport(event) {
            const file = event.target.files[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = (e) => {
                this.markdown = e.target.result;
                ElMessage.success(`File imported: ${file.name}`);
            };
            reader.onerror = () => {
                ElMessage.error('Failed to read file');
            };
            reader.readAsText(file);
        },

        highlightCode() {
            try {
                const content = this.$refs.content;
                if (content) {
                    const blocks = content.querySelectorAll('pre code');
                    blocks.forEach(block => {
                        if (block && !block.classList.contains('hljs')) {
                            hljs.highlightElement(block);
                        }
                    });
                }
            } catch (error) {
                console.error('Code highlighting error:', error);
            }
        }
    },
    mounted() {
        // 初始化时渲染空内容
        this.renderMarkdown();
    },
    beforeUnmount() {
        // 清理定时器
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
    }
};
</script>

<style scoped>
.markdown-pdf-container {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background: var(--bg-page);
    font-family: var(--font-body);
}

/* 工具栏样式 */
.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-md) var(--spacing-lg);
    background: var(--bg-card);
    border-bottom: 1px solid var(--border-color);
    box-shadow: var(--shadow-sm);
    z-index: 10;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: var(--spacing-lg);
}

.title {
    margin: 0;
    color: var(--text-primary);
    font-size: 20px;
    font-weight: 600;
    font-family: var(--font-heading);
}

.stats {
    display: flex;
    gap: var(--spacing-md);
}

.stat-item {
    font-size: 13px;
    color: var(--text-secondary);
    background: var(--bg-secondary);
    padding: var(--spacing-xs) var(--spacing-sm);
    border-radius: var(--radius-sm);
    font-weight: 500;
}

.toolbar-right {
    display: flex;
    gap: var(--spacing-sm);
}

/* 主容器 */
.container {
    display: flex;
    flex: 1;
    gap: 0;
    height: calc(100vh - 80px);
}

/* 编辑器和预览区域 */
.editor,
.md-viewer {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: var(--bg-card);
    border-right: 1px solid var(--border-color);
}

.md-viewer {
    border-right: none;
}

/* 头部标签 */
.editor-header,
.viewer-header {
    padding: var(--spacing-sm) var(--spacing-lg);
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
    font-family: var(--font-body);
}

/* 编辑器文本框 */
textarea {
    flex: 1;
    padding: var(--spacing-lg);
    border: none;
    resize: none;
    font-family: var(--font-mono);
    font-size: 14px;
    line-height: 1.6;
    outline: none;
    background: var(--bg-card);
    color: var(--text-primary);
}

textarea::placeholder {
    color: var(--text-disabled);
}

/* 预览内容区域 */
.viewer-content {
    flex: 1;
    padding: 20px;
    overflow-y: auto;
    background: #ffffff;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e0 #f7fafc;
    /* PDF导出优化 */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeQuality;
    font-feature-settings: "liga" 1, "kern" 1;
}

/* 预览区域滚动条样式 */
.viewer-content::-webkit-scrollbar {
    width: 8px;
}

.viewer-content::-webkit-scrollbar-track {
    background: #f7fafc;
    border-radius: 4px;
}

.viewer-content::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 4px;
}

.viewer-content::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
}

/* Markdown 内容样式 */
.viewer-content :deep(h1),
.viewer-content :deep(h2),
.viewer-content :deep(h3),
.viewer-content :deep(h4),
.viewer-content :deep(h5),
.viewer-content :deep(h6) {
    color: #1f2937;
    font-weight: 600;
    margin-top: 24px;
    margin-bottom: 12px;
    line-height: 1.3;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeQuality;
}

.viewer-content :deep(h1) {
    font-size: 32px;
    border-bottom: 2px solid #e5e7eb;
    padding-bottom: 8px;
}

.viewer-content :deep(h2) {
    font-size: 24px;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 6px;
}

.viewer-content :deep(h3) {
    font-size: 20px;
}

.viewer-content :deep(p) {
    margin-bottom: 16px;
    line-height: 1.7;
    color: #374151;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
    font-size: 16px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeQuality;
}

.viewer-content :deep(code) {
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'SF Mono', Monaco, 'Inconsolata', 'Roboto Mono', 'Source Code Pro', Menlo, 'Ubuntu Mono', monospace;
    font-size: 14px;
    color: #e53e3e;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeQuality;
    font-weight: 500;
}

.viewer-content :deep(pre) {
    background: #1f2937;
    padding: 20px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 16px 0;
    border: 1px solid #374151;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
}

.viewer-content :deep(pre code) {
    background: none;
    padding: 0;
    color: #e5e7eb;
    font-size: 15px;
    font-family: 'SF Mono', Monaco, 'Inconsolata', 'Roboto Mono', 'Source Code Pro', Menlo, 'Ubuntu Mono', monospace;
    line-height: 1.5;
    font-weight: 400;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeQuality;
}

.viewer-content :deep(blockquote) {
    border-left: 4px solid #d1d5db;
    padding-left: 16px;
    margin: 16px 0;
    color: #6b7280;
    font-style: italic;
}

.viewer-content :deep(ul),
.viewer-content :deep(ol) {
    margin-bottom: 16px;
    padding-left: 24px;
}

.viewer-content :deep(li) {
    margin-bottom: 4px;
}

.viewer-content :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
}

.viewer-content :deep(th),
.viewer-content :deep(td) {
    border: 1px solid #e5e7eb;
    padding: 8px 12px;
    text-align: left;
}

.viewer-content :deep(th) {
    background: #f9fafb;
    font-weight: 600;
}


/* 响应式设计 */
@media (max-width: 768px) {
    .toolbar {
        flex-direction: column;
        gap: 16px;
        padding: 16px;
    }

    .toolbar-left {
        flex-direction: column;
        gap: 12px;
        align-items: flex-start;
    }

    .container {
        flex-direction: column;
        height: calc(100vh - 140px);
    }

    .editor,
    .md-viewer {
        height: 50%;
        border-right: none;
        border-bottom: 1px solid #e0e6ed;
    }

    .md-viewer {
        border-bottom: none;
    }
}
</style>